import type { JSX } from 'react';

import { Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import type { AlertAction } from '../../types/alertsConfig';
import { getAlertActionDefinition } from '../../types/alertsConfig';
import AlertActionSelect from './AlertActionSelect';

interface AlertActionRowProps {
  action: AlertAction;
  actionIndex: number;
  alertIndex: number;
  onFunctionChange: (fn: string) => void;
  onParameterChange: (paramKey: string, paramValue: string | number | boolean | null) => void;
  onDelete: () => void;
}

const AlertActionRow = ({
  action,
  actionIndex,
  alertIndex,
  onFunctionChange,
  onParameterChange,
  onDelete,
}: AlertActionRowProps): JSX.Element => {
  const def = getAlertActionDefinition(action.function);
  const selectId = `alert-${alertIndex}-action-${actionIndex}-fn`;
  const urlInputId = `alert-${alertIndex}-action-${actionIndex}-url`;

  const handleFunctionChange = (fn: string): void => {
    if (action.function === 'sendGoogleChatMessage' && fn !== 'sendGoogleChatMessage') {
      onParameterChange('url', null);
    }
    onFunctionChange(fn);
  };

  const isLegacy = def?.status === 'legacy';
  const isChatMessage = action.function === 'sendGoogleChatMessage';
  const isSendWebhook = action.function === 'sendWebhook';

  return (
    <div
      className={
        isLegacy
          ? 'rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-3'
          : 'rounded-lg border border-border-subtle bg-bg-surface-alt p-3 space-y-3'
      }
    >
      <div className="flex items-start gap-2">
        <label className="flex-1 min-w-0 block" htmlFor={selectId}>
          <span className="mb-1 block text-sm font-medium text-text-muted font-body">Función</span>
          <AlertActionSelect id={selectId} value={action.function} onChange={handleFunctionChange} />
        </label>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="mt-7 shrink-0"
          onClick={onDelete}
          aria-label={`Eliminar acción ${actionIndex + 1}`}
        >
          <Trash2 className="size-4" aria-hidden />
        </Button>
      </div>

      {def && <p className="text-xs text-text-muted font-body">{def.description}</p>}

      {isChatMessage && (
        <label className="block" htmlFor={urlInputId}>
          <span className="mb-1 block text-sm font-medium text-text-muted font-body">
            URL de Google Chat <span className="text-error">*</span>
          </span>
          <Input
            id={urlInputId}
            value={typeof action.parameters?.url === 'string' ? action.parameters.url : ''}
            onChange={(e) => onParameterChange('url', e.target.value)}
            placeholder="https://chat.googleapis.com/v1/spaces/…/messages?key=…"
            className="h-10 px-4 py-2"
          />
        </label>
      )}

      {isLegacy && (
        <p className="text-xs text-amber-700 font-body">
          ⚠ Esta función <strong>ya no se ejecuta</strong>. Cámbiala por{' '}
          <strong>&ldquo;Enviar a webhooks&rdquo;</strong> para que la alerta llegue.
        </p>
      )}

      {isSendWebhook && (
        <p className="text-xs text-text-muted font-body">
          ¿No ves tus webhooks?{' '}
          <a href="../webhooks" className="text-primary underline-offset-2 hover:underline">
            Ir a Webhooks →
          </a>
        </p>
      )}
    </div>
  );
};

export default AlertActionRow;
